import React, { useEffect, useState, useRef } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { AppDispatch } from "src/redux/store";
import {
    createCustomFieldsAsync,
    createCustomFieldValuesAsync,
    deleteCustomFieldsValuesAsync,
    getCustomFieldValueListAsync,
    getEditCustomFieldsListAsync,
    updateCustomFieldsAsync,
    updateCustomFieldValuesAsync,
} from "src/redux/actions/omsPageActions";
import Loader from "src/utils/Loader";
import { toast } from "react-toastify";
import { KTIcon } from "src/marketConnect/helpers";
import Select from "react-select";
import CommonAlertModal from "src/utils/modals/CommonAlertModal";
import { DATA_TYPES, OWNER_OPTIONS } from "src/utils/constants";

interface Props {
    show: boolean;
    onClose: () => void;
    editData?: any;
    onSuccess?: () => void;
}

const CreateEditCustomFieldModal: React.FC<Props> = ({
    show,
    onClose,
    editData,
    onSuccess,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const selectStyles = {
        control: (base: any, state: any) => ({
            ...base,
            minHeight: "34px",
            borderColor: state.isFocused ? "#80bdff" : "#ced4da",
            boxShadow: "none",
            "&:hover": {
                borderColor: "#80bdff",
            },
        }),
        valueContainer: (base: any) => ({
            ...base,
            padding: "0 12px",
        }),
        input: (base: any) => ({
            ...base,
            margin: 0,
            padding: 0,
        }),
        indicatorsContainer: (base: any) => ({
            ...base,
            height: "34px",
        }),
        placeholder: (base: any) => ({
            ...base,
            color: "#6c757d",
        }),
    };

    const [form, setForm] = useState<any>({
        display_name: "",
        owner: "",
        data_type: "TEXT",
        help_text: "",
        isRequired: false,
        default_value: "",
        options: [],
        isActive: true,
    });

    const [errors, setErrors] = useState<any>({});
    const [saveLoading, setSaveLoading] = useState(false);
    const isSubmittingRef = useRef(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);
    const [loadingEditData, setLoadingEditData] = useState(false);

    // ================= PREFILL =================
    useEffect(() => {
        if (!show) return;

        const fetchEditData = async () => {
            if (!editData?.id) {
                setForm({
                    display_name: "",
                    owner: "",
                    data_type: "TEXT",
                    help_text: "",
                    isRequired: false,
                    default_value: "",
                    options: [],
                    isActive: true,
                });
                setErrors({});
                return;
            }

            try {
                setLoadingEditData(true);
                // -------- 1st API: field meta --------
                const fieldRes = await dispatch(
                    getEditCustomFieldsListAsync({ id: editData.id })
                ).unwrap();

                const fieldData = fieldRes?.data;

                // -------- 2nd API: option values --------
                const valueRes = await dispatch(
                    getCustomFieldValueListAsync({ id: editData.id })
                ).unwrap();

                // IMPORTANT: your API returns ARRAY directly
                const optionValues = Array.isArray(valueRes?.results?.data) ? valueRes.results.data : [];
                const options = optionValues.map(o => ({
                    id: o.id,
                    api_code: o.api_code,
                    is_default: o.is_default,
                    isActive: o.isActive ?? true,
                }));

                let defaultValue: string | string[] | boolean = "";

                if (fieldData.input_type === "SELECT") {
                    defaultValue = options.find((o) => o.is_default)?.api_code || "";
                } else if (fieldData.input_type === "MULTISELECT") {
                    defaultValue = options.filter((o) => o.is_default).map((o) => o.api_code);
                } else if (fieldData.input_type === "CHECKBOX") {
                    const checkboxValue =
                        fieldData?.default_value?.name ??
                        fieldData?.default_value?.value ??
                        fieldData?.default_value?.default_value ??
                        fieldData?.default_value;

                    defaultValue =
                        checkboxValue === true ||
                        checkboxValue === 1 ||
                        String(checkboxValue).toLowerCase() === "true" ||
                        String(checkboxValue) === "1";
                } else {
                    defaultValue = fieldData?.default_value?.name || "";
                }

                setForm({
                    display_name: fieldData.display_name || "",
                    owner: fieldData.owner || "",
                    data_type: fieldData.input_type || "TEXT",
                    help_text: fieldData.description || "",
                    isRequired: fieldData.isRequired || false,
                    default_value: defaultValue,
                    options: options,
                    isActive: fieldData.isActive ?? true,
                });
                setErrors({});
            } catch (e) {
                console.error("Failed to fetch edit data", e);
            } finally {
                setLoadingEditData(false);
            }
        };

        fetchEditData();
    }, [show, editData?.id, dispatch]);


    // ================= VALIDATION =================
    const validate = () => {
        const err: any = {};

        if (!form.display_name) {
            err.display_name = "Field name is required";
            toast.error("Field name is required");
        }

        if (!form.owner) {
            err.owner = "Owner is required";
            toast.error("Owner is required");
        }

        if (!form.data_type) {
            err.data_type = "Data type is required";
            toast.error("Data type is required");
        }

        if (
            ["SELECT", "MULTISELECT"].includes(form.data_type) &&
            form.options.length === 0
        ) {
            err.options = "At least one option is required";
            toast.error("At least one option is required");
        }

        setErrors(err);

        return Object.keys(err).length === 0;
    };

    // delete option select multiselect
    const handleOptionDelete = (i: number) => {
        const option = form.options[i];

        // If option has an id → ask confirmation
        if (option.id) {
            setPendingDeleteIndex(i);
            setShowDeleteModal(true);
        } else {
            // If new option → just remove
            const updated = form.options.filter((_: any, idx: number) => idx !== i);
            setForm({ ...form, options: updated });
        }
    };

    const handleDeleteConfirmed = async () => {
        if (pendingDeleteIndex === null) return;

        const option = form.options[pendingDeleteIndex];

        try {
            await dispatch(
                deleteCustomFieldsValuesAsync({ id: editData.id, value_id: option.id })
            ).unwrap();
            toast.success("Option deleted successfully");
            // Remove from local state
            const updated = form.options.filter((_: any, idx: number) => idx !== pendingDeleteIndex);
            setForm({ ...form, options: updated });
        } catch (e: any) {
            toast.error(e?.data?.message || "Failed to delete option");
        } finally {
            setPendingDeleteIndex(null);
            setShowDeleteModal(false);
        }
    };

    // ================= SUBMIT =================
    const handleSubmit = async () => {
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;
        setSaveLoading(true);

        const releaseSubmitting = (cooldownMs = 0) => {
            if (cooldownMs > 0) {
                setTimeout(() => {
                    isSubmittingRef.current = false;
                    setSaveLoading(false);
                }, cooldownMs);
            } else {
                isSubmittingRef.current = false;
                setSaveLoading(false);
            }
        };

        if (!validate()) {
            releaseSubmitting(4000);
            return;
        }

        // Base payload for the field itself
        const payload: any = {
            display_name: form.display_name,
            owner: form.owner,
            input_type: form.data_type.toUpperCase(),
            help_text: form.help_text,
            isRequired: form.isRequired,
            isActive: form.isActive,
        };

        if (["SELECT", "MULTISELECT"].includes(form.data_type)) {
            payload.options = (form.options ?? []).map((opt: any) => ({
                name: opt.api_code ?? opt.name ?? opt,
                is_default: Array.isArray(form.default_value)
                    ? form.default_value.includes(opt.api_code ?? opt.name ?? opt)
                    : form.default_value === (opt.api_code ?? opt.name ?? opt),
            }));
        } else if (form.data_type === "CHECKBOX") {
            payload.default_value = !!form.default_value;
        } else {
            payload.default_value = form.default_value ?? "";
        }
        console.log(payload, "final payload");

        try {
            if (editData) {
                const isSelectType = editData.input_type === "SELECT" || editData.input_type === "MULTISELECT";
                const res = await dispatch(
                    updateCustomFieldsAsync({
                        id: editData.id,
                        payloadupdate: payload,
                    })
                ).unwrap();

                if (res?.data?.id) {
                    const fieldId = res.data.id;

                    // NON SELECT / MULTISELECT → SINGLE API CALL
                    if (!isSelectType && res?.data?.default_value?.id) {
                        await dispatch(
                            updateCustomFieldValuesAsync({
                                id: fieldId,
                                value_id: res?.data?.default_value?.id,
                                payloadupdate: {
                                    default_value: form.default_value,
                                    is_default: true,
                                    is_selected: false,
                                    isActive: true,
                                },
                            })
                        ).unwrap();
                    }

                    // SELECT / MULTISELECT → EXISTING + NEW OPTIONS
                    else if (form.options.length) {
                        const existingOptions = form.options.filter(opt => opt.id);
                        const newOptions = form.options.filter(opt => !opt.id);

                        // Update existing options
                        const updateCalls = existingOptions.map((opt: any) => {
                            const payloadupdate = {
                                default_value: opt.api_code,
                                is_default: Array.isArray(form.default_value)
                                    ? form.default_value.includes(opt.api_code)
                                    : form.default_value === opt.api_code,
                                is_selected: false,
                                isActive: opt.isActive ?? true,
                            };

                            return dispatch(
                                updateCustomFieldValuesAsync({
                                    id: fieldId,
                                    value_id: opt.id,
                                    payloadupdate,
                                })
                            ).unwrap();
                        });

                        // Create new options (batch)
                        let createCall = Promise.resolve();
                        if (newOptions.length > 0) {
                            const createPayload = newOptions.map((opt: any) => ({
                                name: opt.api_code,
                                is_default: Array.isArray(form.default_value)
                                    ? form.default_value.includes(opt.api_code)
                                    : form.default_value === opt.api_code,
                                is_selected: false,
                                isActive: opt.isActive ?? true,
                            }));

                            createCall = dispatch(
                                createCustomFieldValuesAsync({
                                    id: fieldId,
                                    payloadupdate: createPayload,
                                })
                            ).unwrap();
                        }

                        await Promise.all([...updateCalls, createCall]);
                    }

                    toast.success(res.message || "Custom field updated successfully");
                }
            } else {
                // Create custom field
                const res = await dispatch(createCustomFieldsAsync(payload)).unwrap();

                if (res?.data?.id) {
                    const fieldId = res.data.id;
                    let optionsPayload: any[] = [];

                    // SELECT / MULTISELECT
                    if (res.data.input_type !== "SELECT" && res.data.input_type !== "MULTISELECT") {
                        // optionsPayload = [
                        //     {
                        //         default_value: form.default_value,
                        //     },
                        // ];

                        optionsPayload = form.options.map((opt: any) => ({
                            ...(opt.id ? { id: opt.id } : {}),
                            name: opt.api_code,
                            is_default: Array.isArray(form.default_value)
                                ? form.default_value.includes(opt.api_code)
                                : form.default_value === opt.api_code,
                        }));

                        // Call API only if payload exists
                        if (optionsPayload.length) {
                            await dispatch(createCustomFieldValuesAsync({
                                id: fieldId, payloadupdate: optionsPayload,
                            })).unwrap();
                        }
                    }


                    // else if (form.options.length) {
                    //     optionsPayload = form.options.map((opt: any) => ({
                    //         ...(opt.id ? { id: opt.id } : {}),
                    //         name: opt.api_code,
                    //         is_default: Array.isArray(form.default_value)
                    //             ? form.default_value.includes(opt.api_code)
                    //             : form.default_value === opt.api_code,
                    //     }));
                    // }
                    // // Call API only if payload exists
                    // if (optionsPayload.length) {
                    //     await dispatch(createCustomFieldValuesAsync({
                    //         id: fieldId, payloadupdate: optionsPayload,
                    //     })).unwrap();
                    // }

                    toast.success("Custom field created successfully");
                }
            }
            onSuccess?.();
        } catch (e: any) {
            const apiMessage = e?.data?.message || e?.error?.message;
            if (apiMessage) {
                toast.error(apiMessage);
                setErrors((prev: any) => ({
                    ...prev,
                    display_name: apiMessage,
                }));
            } else {
                toast.error("Failed to save custom field");
            }
        } finally {
            releaseSubmitting(0);
        }
    };

    // ================= OPTIONS =================
    const addOption = () =>
        setForm({
            ...form,
            options: [
                ...form.options,
                { id: null, api_code: "", is_default: false, isActive: true },
            ],
        });

    const updateOption = (i: number, val: string) => {
        const opts = [...form.options];
        opts[i] = { ...opts[i], api_code: val }; // important: keep the object shape
        setForm({ ...form, options: opts });
    };

    const removeOption = (i: number) => {
        const opts = form.options.filter((_: any, idx: number) => idx !== i);
        setForm({ ...form, options: opts });
    };

    // ================= DEFAULT INPUT =================
    const renderDefaultValue = () => {
        if (form.data_type === "SELECT") {
            const options = form.options.map((o: any) => ({
                label: o.api_code,
                value: o.api_code,
            }));

            return (
                <Select
                    styles={selectStyles}
                    options={options}
                    isMulti={false}
                    value={
                        form.default_value
                            ? options.find(opt => opt.value === form.default_value) || null
                            : null
                    }
                    onChange={(opt) =>
                        setForm({
                            ...form,
                            default_value: opt?.value || "",
                        })
                    }
                    placeholder="Select default"
                    isClearable
                />
            );
        }

        if (form.data_type === "MULTISELECT") {
            const options = form.options.map((o: any) => ({
                label: o.api_code,
                value: o.api_code,
            }));

            return (
                <Select
                    styles={selectStyles}
                    options={options}
                    isMulti
                    value={options.filter(opt =>
                        Array.isArray(form.default_value) &&
                        form.default_value.includes(opt.value)
                    )}
                    onChange={(selected) =>
                        setForm({
                            ...form,
                            default_value: selected ? selected.map((o: any) => o.value) : [],
                        })
                    }
                    placeholder="Select default values"
                    closeMenuOnSelect={false}
                />
            );
        }
        if (form.data_type === "CHECKBOX") {
            return (
                <div className="form-check" style={{ display: "flex", gap: "10px" }}>
                    <input
                        // className="form-check-input"
                        type="checkbox"
                        id="defaultCheckboxValue"
                        checked={!!form.default_value}
                        onChange={(e) =>
                            setForm({
                                ...form,
                                default_value: e.target.checked,
                            })
                        }
                    />
                    <label htmlFor="defaultCheckboxValue">
                        Checked by default
                    </label>
                </div>
            );
        }

        // TEXT, NUMBER, EMAIL, URL, DATE, TIME...
        const typeMap: any = {
            NUMBER: "number",
            EMAIL: "email",
            URL: "url",
            DATE: "date",
            DATE_TIME: "datetime-local",
            TIME: "time",
        };

        return (
            <input
                type={typeMap[form.data_type] || "text"}
                className="form-control"
                value={form.default_value}
                onChange={(e) =>
                    setForm({ ...form, default_value: e.target.value })
                }
                style={{ height: "34px" }}
            />
        );
    };

    return (
        <Modal show={show} onHide={onClose} centered size="lg" backdrop="static">
            {/* HEADER */}
            <div className="d-flex justify-content-between align-items-center px-4 py-3 bg-light border-bottom">
                <h5 className="m-0 fw-bold">
                    {editData ? "Edit Custom Field" : "Create Custom Field"}
                </h5>
                <div className="d-flex gap-2">
                    <button className="btn btn-secondary d-flex align-items-center justify-content-center" onClick={onClose} disabled={saveLoading || loadingEditData}>
                        Cancel
                    </button>
                    <button className="btn btn-primary d-flex align-items-center justify-content-center" onClick={handleSubmit} disabled={saveLoading || loadingEditData}>
                        {saveLoading ? "Saving..." : editData ? "Update" : "Save"}
                    </button>
                </div>
            </div>

            {/* BODY */}
            <Modal.Body className="px-4 pb-4">
                {loadingEditData ? (
                    <div
                        className="d-flex justify-content-center align-items-center"
                        style={{ height: "200px" }}
                    >
                        <Loader variant="section" />
                    </div>
                ) : (
                    <div className="row gy-3">
                        {/* Field Name */}
                        <div className="col-12">
                            <label className="form-label fw-semibold required">
                                Field Name
                            </label>
                            <input
                                className={`form-control ${errors.display_name ? "is-invalid" : ""
                                    }`}
                                value={form.display_name}
                                onChange={(e) =>
                                    setForm({ ...form, display_name: e.target.value })
                                }
                                style={{ height: "34px" }}
                            />
                            <div className="invalid-feedback">{errors.display_name}</div>
                        </div>

                            {/* Owner */}
                            <div className="col-md-6">
                                <label className="form-label fw-semibold required">Owner</label>
                                <Select<{ label: string; value: string }, false>
                                    styles={selectStyles}
                                    isDisabled={!!editData}
                                    options={OWNER_OPTIONS}
                                    value={
                                        form.owner
                                            ? OWNER_OPTIONS.find((item) => item.value === form.owner) || null
                                            : null
                                    }
                                    onChange={(opt) => {
                                        setForm({ ...form, owner: opt?.value || "" });
                                        setErrors((prev: any) => ({ ...prev, owner: "" }));
                                    }}
                                    placeholder="Select owner"
                                />
                                <div className="invalid-feedback d-block">{errors.owner}</div>
                            </div>

                            {/* Data Type */}
                            <div className="col-md-6">
                                <label className="form-label fw-semibold required">Data Type</label>
                                <Select
                                    styles={selectStyles}
                                    isDisabled={!!editData}
                                    options={DATA_TYPES.map(d => ({ label: d, value: d }))}
                                    value={{ label: form.data_type, value: form.data_type }}
                                    onChange={(opt) =>
                                        setForm({
                                            ...form,
                                            data_type: opt?.value || "TEXT",
                                            options: [],
                                            default_value: opt?.value === "CHECKBOX" ? false : "",
                                    })
                                }
                                placeholder="Select data type"
                            />

                        </div>

                        {/* Help Text */}
                        <div className="col-12">
                            <label className="form-label fw-semibold">Help Text</label>
                            <input
                                className="form-control"
                                value={form.help_text}
                                onChange={(e) =>
                                    setForm({ ...form, help_text: e.target.value })
                                }
                                style={{ height: "34px" }}
                                max={500}
                                placeholder="max. 500 character"
                            />
                        </div>

                        {/* OPTIONS */}
                        {["SELECT", "MULTISELECT"].includes(form.data_type) && (
                            <div className="col-12">
                                <div className="fw-semibold mb-2">
                                    Options Count : {form.options.length}
                                </div>

                                {form.options.map((opt: any, i: number) => (
                                    <div key={opt.id ?? i} className="d-flex gap-2 mb-2">
                                        <input
                                            className="form-control"
                                            value={opt.api_code}
                                            onChange={(e) => {
                                                const updated = [...form.options];
                                                updated[i] = { ...updated[i], api_code: e.target.value };
                                                setForm({ ...form, options: updated });
                                            }}
                                        />
                                        <button
                                            className="btn btn-icon btn-bg-light btn-color-danger btn-sm"
                                            onClick={() => handleOptionDelete(i)}
                                        >
                                            <KTIcon iconName="trash" />
                                        </button>
                                    </div>
                                ))}
                                <button
                                    className="btn btn-sm btn-light-primary mt-2"
                                    onClick={addOption}
                                >
                                    + Add Option
                                </button>

                                {errors.options && (
                                    <div className="text-danger mt-1">{errors.options}</div>
                                )}
                            </div>
                        )}

                        {/* DEFAULT VALUE */}
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Default Value</label>
                            {renderDefaultValue()}
                        </div>

                        {/* MANDATORY */}
                        <div className="col-md-6">
                            <label className="form-label fw-semibold">Is Mandatory</label>
                            <div className="d-flex gap-4 mt-2">
                                <label>
                                    <input
                                        type="radio"
                                        checked={form.isRequired === true}
                                        onChange={() =>
                                            setForm({ ...form, isRequired: true })
                                        }
                                    />{" "}
                                    Yes
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        checked={form.isRequired === false}
                                        onChange={() =>
                                            setForm({ ...form, isRequired: false })
                                        }
                                    />{" "}
                                    No
                                </label>
                            </div>
                        </div>

                        {/* STATUS (EDIT ONLY) */}
                        {editData && (
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">Status</label>
                                <div className="d-flex gap-4 mt-2">
                                    <label>
                                        <input
                                            type="radio"
                                            checked={form.isActive === true}
                                            onChange={() =>
                                                setForm({ ...form, isActive: true })
                                            }
                                        />{" "}
                                        Active
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            checked={form.isActive === false}
                                            onChange={() =>
                                                setForm({ ...form, isActive: false })
                                            }
                                        />{" "}
                                        Inactive
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Modal.Body>
            <CommonAlertModal
                title="Delete Item?"
                subtitle="This option will be removed."
                isVisible={showDeleteModal}
                isTermsVisible={false}
                onSuccess={handleDeleteConfirmed}
                onCancel={() => {
                    setPendingDeleteIndex(null);
                    setShowDeleteModal(false);
                }}
                successBtnTitle="Delete"
                cancelBtnTitle="Cancel"
                successBtnClasses="btn btn-sm btn-danger m-2"
            />

        </Modal>
    );
};

export default CreateEditCustomFieldModal;

